import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import {
  fetchKdsBoard,
  fetchKdsDepartments,
  selectKdsDepartments,
  selectKdsIsLoading,
  selectKdsIsMuted,
  selectKdsKpis,
  selectKdsSelectedDepartmentId,
  selectKdsTickets,
  setSelectedDepartmentId,
  toggleKdsMuted,
  transitionKdsTicket,
} from '@store/kds';
import { DropdownSelect } from '@components/common';
import { LogOut, Volume2, VolumeX } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { KitchenTicketStatus } from '@/renderer/types/kds';
import { playKdsNewTicketSound } from '@/renderer/util/kdsSound';
import { KdsBoard } from '@components/kds/KdsBoard';
import { KdsDepartmentGate } from '@components/kds/KdsDepartmentGate';
import { KdsKpiBar } from '@components/kds/KdsKpiBar';
import { KitchenDisplayLiveSync } from '@components/kds/KitchenDisplayLiveSync';

const useLiveClock = (): Date => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(interval);
  }, []);
  return now;
};

export const KitchenDisplayPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUser = useAppSelector(selectCurrentUser);
  const selectedDepartmentId = useAppSelector(selectKdsSelectedDepartmentId);
  const departments = useAppSelector(selectKdsDepartments);
  const tickets = useAppSelector(selectKdsTickets);
  const kpis = useAppSelector(selectKdsKpis);
  const isLoading = useAppSelector(selectKdsIsLoading);
  const isMuted = useAppSelector(selectKdsIsMuted);
  const [transitioningTicketId, setTransitioningTicketId] = useState<
    string | null
  >(null);

  const clock = useLiveClock();

  useEffect(() => {
    dispatch(fetchKdsDepartments());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedDepartmentId) return undefined;
    dispatch(fetchKdsBoard(selectedDepartmentId));
    const interval = window.setInterval(() => {
      dispatch(fetchKdsBoard(selectedDepartmentId));
    }, 30_000);
    return () => window.clearInterval(interval);
  }, [dispatch, selectedDepartmentId]);

  const handleToggleMute = () => {
    const willUnmute = isMuted;
    dispatch(toggleKdsMuted());
    if (willUnmute) {
      playKdsNewTicketSound();
    }
  };

  const handleAdvance = async (
    ticketId: string,
    toStatus: KitchenTicketStatus,
  ) => {
    if (!selectedDepartmentId) return;
    setTransitioningTicketId(ticketId);
    await dispatch(
      transitionKdsTicket({
        ticketId,
        toStatus,
        departmentId: selectedDepartmentId,
      }),
    );
    setTransitioningTicketId(null);
  };

  return (
    <div className="flex h-screen flex-col bg-gray-50">
      <KitchenDisplayLiveSync />

      <header className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
        <h1 className="text-2xl font-bold text-black">
          {currentUser?.outlet?.name || 'Kitchen Display'}
        </h1>

        <div className="flex items-center gap-4">
          <span className="font-mono text-xl text-gray-700">
            {clock.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          <button
            type="button"
            onClick={handleToggleMute}
            aria-label={
              isMuted ? 'Unmute new-order sound' : 'Mute new-order sound'
            }
            className="rounded-full p-2.5 text-gray-600 hover:bg-gray-100 hover:text-black"
          >
            {isMuted ? <VolumeX size={24} /> : <Volume2 size={24} />}
          </button>
          <button
            type="button"
            onClick={() => navigate('/sales')}
            className="flex items-center gap-1.5 rounded-full px-3 py-2 text-base text-gray-600 hover:bg-gray-100 hover:text-black"
          >
            <LogOut size={20} />
            Exit
          </button>
        </div>
      </header>

      {!selectedDepartmentId ? (
        <KdsDepartmentGate
          departments={departments}
          isLoading={isLoading && departments.length === 0}
          onSelect={(departmentId) =>
            dispatch(setSelectedDepartmentId(departmentId))
          }
        />
      ) : (
        <>
          <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white px-6 py-3">
            <div className="w-64">
              <DropdownSelect
                value={selectedDepartmentId ?? undefined}
                options={departments.map((d) => ({
                  value: d.id,
                  label: d.name,
                }))}
                onChange={(id) => dispatch(setSelectedDepartmentId(id))}
                placeholder="Select department"
              />
            </div>
            <KdsKpiBar kpis={kpis} />
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6">
            <KdsBoard
              tickets={tickets}
              transitioningTicketId={transitioningTicketId}
              onAdvance={handleAdvance}
            />
          </div>
        </>
      )}
    </div>
  );
};
